import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import DateField from "@/components/DateField";
import ParentProjectPicker from "./ParentProjectPicker";
import SphSubmissionsList from "@/components/project-forms/SphSubmissionsList";

// 16-WO-SPH-DATE-UX-R1: derive latest SPH date (ISO → dd/mm/yyyy display).
function pickLatestIso(list) {
  const arr = Array.isArray(list) ? list : [];
  let latest = "";
  for (const r of arr) {
    const d = (r?.date || "").trim();
    if (d && d > latest) latest = d;
  }
  return latest;
}
function deriveSphLatestDMY(list, fallbackIso) {
  const iso = pickLatestIso(list) || (fallbackIso || "").slice(0, 10);
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : "";
}

/** WO fields for EditProjectDialog. */
export default function EditWOFields({ project, form, setForm, canChangeParent }) {
  return (
    <>
      <ParentProjectPicker
        project={project}
        value={form.parent_project_id}
        onChange={(id) => setForm({ ...form, parent_project_id: id })}
        disabled={!canChangeParent}
      />
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label className="overline text-slate-600">WO Number</Label>
          <Input data-testid="edit-wo-number" required value={form.wo_number} onChange={(e) => setForm({ ...form, wo_number: e.target.value })} className="rounded-none" />
        </div>
        <div className="space-y-1.5">
          <Label className="overline text-slate-600">WO Date</Label>
          <DateField testid="edit-wo-date" value={form.wo_date} onChange={(v) => setForm({ ...form, wo_date: v })} />
        </div>
      </div>
      <div className="space-y-1.5">
        <Label className="overline text-slate-600">Judul WO <span className="text-slate-400 italic normal-case text-[10px]">(opsional)</span></Label>
        <Input data-testid="edit-wo-title" value={form.wo_title || ""} onChange={(e) => setForm({ ...form, wo_title: e.target.value })} className="rounded-none" placeholder="e.g. Renovasi Fasade Outlet Adam Malik" />
      </div>
      <div className="space-y-1.5">
        <Label className="overline text-slate-600">Work Description <span className="text-slate-400 italic normal-case text-[10px]">(deskripsi pekerjaan)</span></Label>
        <textarea
          data-testid="edit-wo-work-description"
          value={form.work_description || ""}
          onChange={(e) => setForm({ ...form, work_description: e.target.value })}
          rows={3}
          placeholder="Uraikan lingkup pekerjaan WO ini…"
          className="w-full rounded-none border border-slate-300 bg-white px-3 py-2 text-sm placeholder:text-slate-400 focus:outline-none focus:border-[#FF5A1F] resize-y"
        />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label className="overline text-slate-600">PO Number</Label>
          <Input data-testid="edit-wo-po-number" value={form.po_number} onChange={(e) => setForm({ ...form, po_number: e.target.value })} className="rounded-none" />
        </div>
        <div className="space-y-1.5">
          <Label className="overline text-slate-600">PO Date</Label>
          <DateField testid="edit-wo-po-date" value={form.po_date} onChange={(v) => setForm({ ...form, po_date: v })} />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label className="overline text-slate-600">SPH Submit Date (latest)</Label>
          {/* 16-WO-SPH-DATE-UX-R1: Read-only, auto-derived from Riwayat SPH. */}
          <div
            data-testid="edit-wo-sph-latest-readonly"
            className="w-full h-9 border border-slate-200 bg-slate-50 px-3 flex items-center text-xs tabular-nums text-[#0B1D3A] font-semibold"
          >
            {deriveSphLatestDMY(form.sph_submissions, form.sph_submit_date) || <span className="text-slate-400 italic font-normal">Belum ada SPH</span>}
          </div>
          <p className="text-[10px] text-slate-500 italic">Read-only — otomatis mengambil tanggal SPH terbaru dari Riwayat SPH di bawah.</p>
        </div>
        <div className="space-y-1.5">
          <Label className="overline text-slate-600">Site Survey Date</Label>
          <DateField testid="edit-wo-survey" value={form.site_survey_date} onChange={(v) => setForm({ ...form, site_survey_date: v })} />
        </div>
      </div>
      <SphSubmissionsList
        value={form.sph_submissions || []}
        onChange={(next) => setForm({ ...form, sph_submissions: next, sph_submit_date: pickLatestIso(next) || form.sph_submit_date || "" })}
        testidPrefix="edit-wo"
      />
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label className="overline text-slate-600">Project Done Date</Label>
          <DateField testid="edit-wo-done" value={form.project_done_date} onChange={(v) => setForm({ ...form, project_done_date: v })} />
        </div>
        {/* 2026-06-11 (user request): tambahkan input tanggal invoice terkirim.
            Field ini sudah didukung backend (_EDITABLE_WO) & lifecycle
            "Pembuatan Invoice" membaca nilainya. */}
        <div className="space-y-1.5">
          <Label className="overline text-slate-600">Invoice Sent Date <span className="text-slate-400 italic normal-case text-[10px]">(tanggal invoice terkirim)</span></Label>
          <DateField testid="edit-wo-invoice-sent" value={form.invoice_sent_date} onChange={(v) => setForm({ ...form, invoice_sent_date: v })} />
        </div>
      </div>
      <div className="space-y-1.5">
        {/* 2026-06-20 (aturan tri-state ketat): kategori WO eksplisit 3 pilihan —
            true=Urgent Case, false=Non-Urgent Case, kosong=Belum Diklasifikasikan.
            Memilih "Belum Diklasifikasikan" TIDAK menghapus kategori yang sudah
            ada (field tidak dikirim bila null). */}
        <Label className="overline text-slate-600">
          Kategori WO <span className="text-slate-400 italic normal-case text-[10px]">(menentukan alur kerja chevron monitoring)</span>
        </Label>
        <div className="flex gap-1" data-testid="edit-wo-category">
          {[
            { v: null, label: "Belum Diklasifikasikan" },
            { v: true, label: "Urgent Case" },
            { v: false, label: "Non-Urgent Case" },
          ].map((o) => (
            <button
              key={String(o.v)}
              type="button"
              data-testid={`edit-wo-category-${o.v === null ? "unclassified" : o.v ? "urgent" : "non-urgent"}`}
              onClick={() => setForm({ ...form, is_urgent: o.v })}
              className={`flex-1 px-2 py-2 text-[10px] uppercase tracking-wider font-semibold border transition-colors ${
                form.is_urgent === o.v
                  ? o.v === true ? "bg-rose-700 border-rose-700 text-white" : o.v === false ? "bg-[#0B1D3A] border-[#0B1D3A] text-white" : "bg-amber-100 border-amber-400 text-amber-800"
                  : "bg-white border-slate-300 text-slate-600 hover:bg-slate-50"
              }`}
            >
              {o.label}
            </button>
          ))}
        </div>
        {(project?.is_urgent === true || project?.is_urgent === false) && form.is_urgent === null && (
          <div className="text-[10px] text-amber-700 italic">Kategori tersimpan saat ini: {project.is_urgent === true ? "Urgent Case" : "Non-Urgent Case"} — memilih "Belum Diklasifikasikan" tidak akan menghapusnya (field tidak dikirim).</div>
        )}
      </div>
      <div className="space-y-1.5">
        <Label className="overline text-slate-600">
          Kategori WO <span className="text-slate-400 italic normal-case text-[10px]">(catatan manual — muncul di kolom &quot;Kategori WO&quot; tabel &amp; PDF)</span>
        </Label>
        <textarea
          data-testid="edit-wo-perlu-tindakan"
          value={form.perlu_tindakan_manual || ""}
          onChange={(e) => setForm({ ...form, perlu_tindakan_manual: e.target.value })}
          rows={2}
          maxLength={200}
          placeholder="mis. Follow-up PO ke pak Budi, target 30/8"
          className="w-full rounded-none border border-slate-300 bg-white px-3 py-2 text-sm placeholder:text-slate-400 focus:outline-none focus:border-[#FF5A1F] resize-y"
        />
        <p className="text-[10px] text-slate-500 italic">Kosongkan bila tidak ada catatan khusus.</p>
      </div>
    </>
  );
}
